import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 5000;
const FLARE_POLL_MS = 4000;

const CHANNELS = [
  { id: "global", label: "Global", color: "#FCD34D", glow: "rgba(252,211,77,0.65)", testid: "chat-tab-global" },
  { id: "guild",  label: "Guild",  color: "#34D399", glow: "rgba(52,211,153,0.65)", testid: "chat-tab-guild"  },
  { id: "system", label: "System", color: "#F8FAFC", glow: "rgba(248,250,252,0.55)", testid: "chat-tab-system" },
];

// 📺 SYSTEM channel event-type filter chips (Phoenix Herald taxonomy)
const SYSTEM_FILTERS = [
  { id: "all",         label: "All",         icon: "📺", match: () => true },
  { id: "rebirths",    label: "Rebirths",    icon: "🔥", match: (e) => e === "hero_rebirth" || e === "pet_tier_up" },
  { id: "eggs",        label: "Eggs",        icon: "🥚", match: (e) => /egg_drop$/.test(e || "") },
  { id: "bosses",      label: "Bosses",      icon: "🐲", match: (e) => e === "wild_boss_spawn" || e === "summit_cleared" },
  { id: "festival",    label: "Festival",    icon: "🎆", match: (e) => (e || "").startsWith("festival_") },
  { id: "streaks",     label: "Streaks",     icon: "👑", match: (e) => e === "streak_milestone" },
  { id: "reset",       label: "Daily",       icon: "🌅", match: (e) => e === "daily_reset" },
];

const TIER_COST = { phoenix_small: 10, phoenix_large: 75 };

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function ChatLine({ msg, channelColor, isMe }) {
  const tierBorder =
    msg.tier === "phoenix_large" ? "border-l-4 border-orange-500" :
    msg.tier === "phoenix_small" ? "border-l-4 border-yellow-400" :
    "";
  const adminBadge = msg.is_admin ? (
    <span
      className="ml-1 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-widest bg-fuchsia-700/80 text-white"
      style={{ borderRadius: 2 }}
    >
      Phoenix Council
    </span>
  ) : null;
  return (
    <div
      data-testid={`chat-line-${msg.message_id}`}
      className={`px-3 py-1.5 ${tierBorder} ${msg.deleted ? "opacity-40 italic" : ""}`}
    >
      <div className="flex items-baseline gap-1.5 text-[11px] opacity-70">
        <span style={{ color: channelColor }} className="font-semibold">
          {msg.sender_name || "Unknown"}
        </span>
        <span className="text-white/40">Lv {msg.sender_level || 1}</span>
        {adminBadge}
        <span className="ml-auto text-white/40 text-[10px]">{fmtTime(msg.created_at)}</span>
      </div>
      <div
        className="text-sm leading-snug break-words"
        style={{ color: channelColor, textShadow: `0 0 6px ${channelColor}22` }}
      >
        {msg.text}
      </div>
    </div>
  );
}

export default function ChatDock() {
  const { user, isAuthed } = useAuth();
  const isAuthenticated = isAuthed;
  const [open, setOpen]       = useState(false);
  const [channel, setChannel] = useState("global");
  const [systemFilter, setSystemFilter] = useState("all");
  const [text, setText]       = useState("");
  const [state, setState]     = useState(null);
  const [msgsByCh, setMsgsByCh] = useState({ global: [], guild: [], system: [] });
  const [shopOpen, setShopOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const lastChannelRef = useRef("global");

  // Auth uses cookies (axios.defaults.withCredentials = true), no need to
  // forward an Authorization header. Re-fetch state when login state flips.

  // Load state
  const refreshState = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/chat/state`);
      setState(data);
    } catch (e) {
      // silently ignore — likely 401 if not logged in
    }
  }, []);

  // Pull messages for current channel
  const refreshChannel = useCallback(async (ch) => {
    try {
      const { data } = await axios.get(`${API}/chat/${ch}`);
      setMsgsByCh((prev) => ({ ...prev, [ch]: data.messages || [] }));
    } catch (e) { /* ignore */ }
  }, []);

  // Boot + poll
  useEffect(() => {
    if (!isAuthenticated) return;
    refreshState();
    refreshChannel(channel);
    const iv = setInterval(() => {
      if (!open) return;
      refreshChannel(channel);
    }, POLL_MS);
    return () => clearInterval(iv);
  }, [isAuthenticated, channel, open, refreshState, refreshChannel]);

  // Logout clears local view (server history persists)
  useEffect(() => {
    if (!isAuthenticated) {
      setMsgsByCh({ global: [], guild: [], system: [] });
      setOpen(false);
    }
  }, [isAuthenticated]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [open, channel, msgsByCh]);

  if (!isAuthenticated) return null;

  const channelMeta = CHANNELS.find((c) => c.id === channel) || CHANNELS[0];
  const rawMsgs = msgsByCh[channel] || [];
  const isAdmin = !!state?.is_admin;
  // 📺 SYSTEM channel — filter by event-type chips (Phoenix Herald taxonomy)
  const activeFilter = SYSTEM_FILTERS.find((f) => f.id === systemFilter) || SYSTEM_FILTERS[0];
  const msgs = channel === "system"
    ? rawMsgs.filter((m) => activeFilter.match(m.event_type))
    : rawMsgs;
  // Count of SYSTEM events received today (for the live badge)
  const systemLiveCount = (msgsByCh.system || []).filter((m) => {
    if (!m.created_at) return false;
    const d = new Date(m.created_at);
    return Date.now() - d.getTime() < 24 * 3600 * 1000;
  }).length;

  async function handleSend(tier = "quartz") {
    if (!text.trim()) return;
    if (sending) return;
    if (channel === "system" && !isAdmin) {
      toast.error("System channel is Phoenix Council only.");
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/chat/send`,
        { channel, text: text.trim(), tier }
      );
      setText("");
      setState(data.state || state);
      // Optimistically append & re-fetch
      setMsgsByCh((prev) => ({
        ...prev,
        [channel]: [...(prev[channel] || []), data.message].slice(-200),
      }));
      if (data.flare) {
        // Notify the global PhoenixFlareBanner immediately
        window.dispatchEvent(new CustomEvent("omni:phoenix-flare-fired", { detail: data.flare }));
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't send. Try again.";
      toast.error(detail);
    } finally {
      setSending(false);
    }
  }

  async function handleBuy(item) {
    try {
      const { data } = await axios.post(`${API}/chat/shop/buy`, { item, qty: 1 });
      setState(data.state || state);
      toast.success(`Purchased ${state?.shop?.[item]?.label || item} (−${state?.shop?.[item]?.cost_od} OD).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purchase failed.");
    }
  }

  async function clearLocalLog() {
    setMsgsByCh((prev) => ({ ...prev, [channel]: [] }));
    toast.message("Local chat log cleared. Server history remains.");
  }

  async function adminDelete(messageId) {
    if (!isAdmin) return;
    if (!window.confirm("Delete this message for everyone?")) return;
    try {
      await axios.delete(`${API}/chat/message/${messageId}`);
      refreshChannel(channel);
    } catch (e) {
      toast.error("Delete failed.");
    }
  }

  async function adminMute(targetUserId) {
    if (!isAdmin) return;
    const mins = prompt("Mute for how many minutes? (1–10080)", "30");
    if (!mins) return;
    try {
      await axios.post(`${API}/chat/admin/mute`, { target_user_id: targetUserId, minutes: parseInt(mins, 10) });
      toast.success("User muted.");
    } catch (e) {
      toast.error("Mute failed.");
    }
  }

  // Compact pill (closed state)
  if (!open) {
    return (
      <button
        data-testid="chat-dock-open"
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-[80] flex items-center gap-2 px-4 py-2.5 bg-black border-2 border-[#FCD34D] shadow-2xl hover:scale-105 transition-transform"
        style={{ borderRadius: 999 }}
      >
        <span className="text-lg" aria-hidden>💬</span>
        <span className="text-[#FCD34D] font-bold uppercase tracking-widest text-xs">Realm Chat</span>
        <span className="ml-1 text-[10px] px-2 py-0.5 bg-[#FCD34D] text-black font-black" style={{ borderRadius: 999 }}>
          {state?.free_global_left ?? 10}/{state?.daily_free_cap ?? 10}
        </span>
      </button>
    );
  }

  return (
    <div
      data-testid="chat-dock"
      className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-[80] w-[92vw] max-w-[380px] bg-[#0A0A0A]/95 border-2 border-[#1f1f1f] shadow-2xl backdrop-blur-md"
      style={{ borderRadius: 8, boxShadow: `0 0 40px ${channelMeta.glow}` }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
        <div className="flex items-center gap-1">
          {CHANNELS.map((c) => (
            <button
              key={c.id}
              data-testid={c.testid}
              onClick={() => setChannel(c.id)}
              className="relative px-2.5 py-1 text-[10px] font-black uppercase tracking-widest transition-all"
              style={{
                color: channel === c.id ? "#0A0A0A" : c.color,
                background: channel === c.id ? c.color : "transparent",
                border: `1px solid ${c.color}`,
                borderRadius: 4,
                textShadow: channel === c.id ? "none" : `0 0 6px ${c.glow}`,
              }}
            >
              {c.label}
              {c.id === "system" && systemLiveCount > 0 && (
                <span
                  data-testid="chat-system-live-badge"
                  className="absolute -top-1.5 -right-1.5 text-[9px] font-black px-1 py-0.5 bg-rose-500 text-white animate-pulse"
                  style={{ borderRadius: 999, minWidth: 16, lineHeight: 1 }}
                >
                  {systemLiveCount > 99 ? "99+" : systemLiveCount}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <button
            data-testid="chat-shop-toggle"
            onClick={() => setShopOpen((v) => !v)}
            title="Bullhorn & Phoenix Quills"
            className="px-2 py-1 text-[10px] text-[#FCD34D] hover:bg-white/5"
          >
            🛒
          </button>
          <button
            data-testid="chat-clear-local"
            onClick={clearLocalLog}
            title="Clear local view"
            className="px-2 py-1 text-[10px] text-white/60 hover:bg-white/5"
          >
            ⌫
          </button>
          <button
            data-testid="chat-dock-close"
            onClick={() => setOpen(false)}
            title="Hide"
            className="px-2 py-1 text-sm text-white/70 hover:bg-white/5"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Shop drawer */}
      {shopOpen && (
        <div data-testid="chat-shop" className="px-3 py-2 border-b border-white/10 bg-white/[0.02] space-y-1.5">
          {Object.entries(state?.shop || {}).map(([key, item]) => {
            const owned =
              key === "small_bullhorn" ? state?.small_bullhorns :
              key === "flare_small"    ? state?.flare_small :
              key === "flare_large"    ? state?.flare_large : 0;
            return (
              <div key={key} className="flex items-center gap-2 text-xs">
                <span className="text-base" aria-hidden>{item.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold truncate">{item.label} <span className="text-white/40 font-normal">· owned {owned || 0}</span></div>
                  <div className="text-white/50 text-[10px] truncate">{item.blurb}</div>
                </div>
                <button
                  data-testid={`chat-buy-${key}`}
                  onClick={() => handleBuy(key)}
                  className="px-2 py-1 bg-[#FCD34D] text-black font-bold text-[10px] uppercase tracking-widest hover:bg-yellow-200"
                  style={{ borderRadius: 3 }}
                >
                  {item.cost_od} OD
                </button>
              </div>
            );
          })}
          <div className="text-[10px] text-white/40 pt-1">
            OD Balance: <span className="text-[#FCD34D] font-bold">{state?.omni_dollars ?? 0}</span>
          </div>
        </div>
      )}

      {/* 📺 SYSTEM filter chips — only when viewing the System channel */}
      {channel === "system" && (
        <div className="px-2 py-1.5 border-b border-white/10 bg-white/[0.02] flex flex-wrap gap-1"
             data-testid="chat-system-filters">
          {SYSTEM_FILTERS.map((f) => {
            const active = systemFilter === f.id;
            return (
              <button
                key={f.id}
                data-testid={`chat-system-filter-${f.id}`}
                onClick={() => setSystemFilter(f.id)}
                className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider transition-all flex items-center gap-1"
                style={{
                  color: active ? "#0A0A0A" : "#F8FAFC",
                  background: active ? "#F8FAFC" : "transparent",
                  border: "1px solid rgba(248,250,252,0.4)",
                  borderRadius: 999,
                }}
              >
                <span aria-hidden>{f.icon}</span>
                <span>{f.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Messages list (Whispering Quartz scroll) */}
      <div
        ref={scrollRef}
        data-testid={`chat-log-${channel}`}
        className="h-[280px] overflow-y-auto py-1 text-white/90"
        style={{ scrollbarWidth: "thin" }}
      >
        {msgs.length === 0 ? (
          <div className="px-3 py-8 text-center text-white/30 text-xs">
            {channel === "guild" && !state?.guild_id
              ? "Join a guild to unlock /guild chat."
              : channel === "system" && systemFilter !== "all"
              ? `No ${activeFilter.label.toLowerCase()} events yet. Try another filter.`
              : channel === "system"
              ? "📡 The Herald is quiet... events will appear here as the realm moves."
              : "No whispers yet. Be the first to break the silence."}
          </div>
        ) : msgs.map((m) => (
          <div key={m.message_id} className="group relative">
            <ChatLine
              msg={m}
              channelColor={channelMeta.color}
              isMe={m.sender_id === user?.user_id}
            />
            {isAdmin && !m.deleted && (
              <div className="absolute right-1 top-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                <button
                  data-testid={`chat-admin-delete-${m.message_id}`}
                  onClick={() => adminDelete(m.message_id)}
                  className="px-1.5 py-0.5 bg-red-700/80 text-white text-[9px] uppercase tracking-widest hover:bg-red-600"
                  style={{ borderRadius: 2 }}
                >
                  Del
                </button>
                {m.sender_id !== user?.user_id && (
                  <button
                    data-testid={`chat-admin-mute-${m.sender_id}`}
                    onClick={() => adminMute(m.sender_id)}
                    className="px-1.5 py-0.5 bg-purple-700/80 text-white text-[9px] uppercase tracking-widest hover:bg-purple-600"
                    style={{ borderRadius: 2 }}
                  >
                    Mute
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Input */}
      <div className="border-t border-white/10 p-2">
        {channel === "global" && (
          <div className="flex items-center justify-between text-[10px] mb-1.5">
            <span className="text-white/50">
              Free: <span className="text-[#FCD34D] font-bold">{state?.free_global_left ?? 0}</span> / {state?.daily_free_cap ?? 10}
              {" · "}
              Bullhorns: <span className="text-[#FCD34D] font-bold">{state?.small_bullhorns ?? 0}</span>
            </span>
            <span className="text-white/40">
              🔥 {state?.flare_small ?? 0}  ·  🪶 {state?.flare_large ?? 0}
            </span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <input
            data-testid="chat-input"
            type="text"
            maxLength={300}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend("quartz"); } }}
            placeholder={
              channel === "system" && !isAdmin ? "System is Phoenix Council only…" :
              channel === "guild" && !state?.guild_id ? "Join a guild to chat here…" :
              `Speak to the ${channelMeta.label}…`
            }
            disabled={
              sending ||
              (channel === "system" && !isAdmin) ||
              (channel === "guild" && !state?.guild_id) ||
              !!state?.muted_until
            }
            className="flex-1 px-2.5 py-1.5 bg-black border border-white/15 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FCD34D]/80"
            style={{ borderRadius: 4 }}
          />
          <button
            data-testid="chat-send"
            onClick={() => handleSend("quartz")}
            disabled={sending || !text.trim()}
            className="px-3 py-1.5 bg-[#FCD34D] text-black font-black text-xs uppercase tracking-widest disabled:opacity-40 hover:bg-yellow-200"
            style={{ borderRadius: 4 }}
          >
            Send
          </button>
        </div>
        {channel === "global" && text.trim() && (
          <div className="flex items-center gap-1.5 mt-1.5">
            <button
              data-testid="chat-send-flare-small"
              onClick={() => handleSend("phoenix_small")}
              disabled={sending || (state?.flare_small ?? 0) <= 0}
              className="flex-1 px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-yellow-400 text-yellow-300 disabled:opacity-40 hover:bg-yellow-400/10"
              style={{ borderRadius: 3 }}
              title="Ignite a 30s Phoenix Flare banner across the realm"
            >
              🔥 Short Flare ({state?.flare_small ?? 0})
            </button>
            <button
              data-testid="chat-send-flare-large"
              onClick={() => handleSend("phoenix_large")}
              disabled={sending || (state?.flare_large ?? 0) <= 0}
              className="flex-1 px-2 py-1 text-[10px] font-bold uppercase tracking-widest border border-orange-500 text-orange-400 disabled:opacity-40 hover:bg-orange-500/10"
              style={{ borderRadius: 3 }}
              title="Ignite a 90s Phoenix Flare banner across the realm"
            >
              🪶 Long Flare ({state?.flare_large ?? 0})
            </button>
          </div>
        )}
        {state?.muted_until && (
          <div className="text-[10px] text-red-400 mt-1 text-center">
            You are muted until {new Date(state.muted_until).toLocaleString()}.
          </div>
        )}
      </div>
    </div>
  );
}
